import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Check, CheckCircle, Zap, Shield, MessageSquare, Crown, Sparkles, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading, api, refreshUser, isSubscribed, getTrialDaysLeft, canSell } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [startingTrial, setStartingTrial] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(null);
  const [validatingPromo, setValidatingPromo] = useState(false);

  const sessionId = searchParams.get('session_id');
  const packageCode = searchParams.get('package');

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login', { state: { from: '/subscription' } });
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (sessionId) {
      if (packageCode) {
        // It's a package payment
        pollPackageStatus(sessionId);
      } else {
        // Regular subscription
        pollPaymentStatus(sessionId);
      }
    }
  }, [sessionId, packageCode]);

  const pollPackageStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please check your email for confirmation.');
      setCheckingPayment(false);
      return;
    }

    setCheckingPayment(true);
    try {
      const res = await api().get(`/subscription/package-status/${sessionId}`);
      
      if (res.data.status === 'completed') {
        toast.success('Payment successful! Your 2-year seller package is now active.');
        await refreshUser();
        setCheckingPayment(false);
        navigate('/dashboard', { replace: true });
        return;
      }

      // Continue polling
      setTimeout(() => pollPackageStatus(sessionId, attempts + 1), pollInterval);
    } catch (err) {
      console.error('Error checking package status:', err);
      setTimeout(() => pollPackageStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please check your email for confirmation.');
      setCheckingPayment(false);
      return;
    }

    setCheckingPayment(true);
    try {
      const res = await api().get(`/subscription/status/${sessionId}`);
      
      if (res.data.payment_status === 'paid') {
        toast.success('Payment successful! Your subscription is now active.');
        await refreshUser();
        setCheckingPayment(false);
        navigate('/subscription', { replace: true });
        return;
      } else if (res.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setCheckingPayment(false);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (err) {
      console.error('Error checking payment status:', err);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  const handleStartTrial = async () => {
    setStartingTrial(true);
    try {
      await api().post('/seller/start-trial');
      toast.success('Your 30-day free trial has started! You can now create listings.');
      await refreshUser();
      navigate('/create-listing');
    } catch (err) {
      console.error('Failed to start trial', err);
      toast.error(err.response?.data?.detail || 'Failed to start trial');
    } finally {
      setStartingTrial(false);
    }
  };

  const validatePromoCode = async () => {
    if (!promoCode.trim()) return;
    
    const code = promoCode.trim().toUpperCase();
    
    // Check if it's the 2YEAR500 paid package code
    if (code === '2YEAR500') {
      setPromoApplied({
        code: code,
        description: '2-Year Seller Package - $500 one-time payment',
        paid_package: true,
        price: 500,
        years: 2
      });
      toast.success('2-Year Package code applied! Click "Buy 2-Year Package" to proceed.');
      return;
    }
    
    setValidatingPromo(true);
    try {
      // Actually APPLY the promo code (not just validate)
      const res = await api().post('/promo/apply', { code: promoCode });
      
      // Success - code was applied!
      setPromoApplied({
        code: res.data.promo_code_used,
        description: res.data.message,
        free_for_life: res.data.free_for_life
      });
      
      toast.success(res.data.message);
      
      // Refresh user data to update banners
      await refreshUser();
      
      // If free for life, redirect to dashboard after a short delay
      if (res.data.free_for_life) {
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to apply promo code';
      toast.error(errorMsg);
      setPromoApplied(null);
    } finally {
      setValidatingPromo(false);
    }
  };

  const handlePackageCheckout = async () => {
    if (!promoApplied?.paid_package) return;
    
    setLoading(true);
    try {
      const res = await api().post('/subscription/package-checkout', {
        promo_code: promoApplied.code,
        origin_url: window.location.origin
      });
      
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      console.error('Failed to create package checkout', err);
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    if (!user) {
      navigate('/login', { state: { from: '/subscription' } });
      return;
    }

    setLoading(true);
    try {
      const res = await api().post('/subscription/checkout', {
        origin_url: window.location.origin,
        plan: selectedPlan,
        promo_code: promoApplied ? promoApplied.code : null
      });
      
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      console.error('Failed to create checkout session', err);
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const trialDays = getTrialDaysLeft();
  const subscribed = user?.subscription_status === 'active';
  const isSeller = user?.is_seller;
  const onTrial = user?.subscription_status === 'trial';
  const isFreeForLife = user?.subscription_status === 'free_for_life' || user?.promo_code_used === 'CMFFL26EF';

  const plans = [
    { id: 'weekly', name: 'Weekly', price: 3, period: 'week' },
    { id: 'monthly', name: 'Monthly', price: 7.99, period: 'month', popular: true },
    { id: 'yearly', name: 'Yearly', price: 88, period: 'year', savings: 'Save ~8%' }
  ];

  const features = [
    { icon: Zap, text: 'Unlimited listings' },
    { icon: Shield, text: 'No commission on sales' },
    { icon: MessageSquare, text: 'Direct messaging with buyers' },
    { icon: Crown, text: 'Before & after photo showcase' }
  ];

  if (!user) return null;

  // Show loading state while auth is being checked
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-stone-300 border-t-teal-600 rounded-full" />
      </div>
    );
  }

  // VIP users don't need to subscribe
  if (isFreeForLife) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] py-12">
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-gradient-to-br from-purple-50 to-white border-2 border-purple-200 rounded-lg p-8 text-center">
            <div className="text-5xl mb-4">🌟</div>
            <h1 className="text-3xl font-bold text-purple-800 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              VIP Member
            </h1>
            <p className="text-xl text-purple-600 mb-4">Free for Life!</p>
            <div className="bg-white rounded-lg p-4 mb-6">
              <p className="text-stone-600">
                Your promo code <span className="font-mono font-bold text-purple-700">{user.promo_code_used}</span> gives you unlimited access to all features:
              </p>
              <ul className="mt-4 space-y-2 text-left max-w-xs mx-auto">
                <li className="flex items-center gap-2 text-stone-700">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Unlimited listings
                </li>
                <li className="flex items-center gap-2 text-stone-700">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  No subscription fees - ever!
                </li>
                <li className="flex items-center gap-2 text-stone-700">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Free tradie advertising
                </li>
                <li className="flex items-center gap-2 text-stone-700">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  All premium features included
                </li>
              </ul>
            </div>
            <Link to="/dashboard">
              <Button className="bg-purple-700 hover:bg-purple-800">
                Go to Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9] py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Checking Payment */}
        {checkingPayment && (
          <div className="bg-white border border-stone-200 rounded-sm p-8 mb-8 text-center">
            <div className="animate-spin h-8 w-8 border-2 border-stone-300 border-t-teal-600 rounded-full mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-stone-900">Processing your payment...</h3>
            <p className="text-stone-500 mt-2">Please wait while we confirm your subscription.</p>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-600 mb-2">Start Selling</p>
          <h1 className="text-4xl font-bold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            Become a Seller
          </h1>
          <p className="text-stone-500 mt-4 max-w-xl mx-auto">
            Ready to showcase your crafts? Start with a 30-day free trial, then choose the plan that works for you.
          </p>
        </div>

        {/* Current Status */}
        {subscribed ? (
          <div className="bg-green-50 border border-green-200 rounded-sm p-6 mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="font-semibold text-green-800 flex items-center gap-2">
                  <Check className="h-5 w-5" />
                  Active Subscription - {user.subscription_plan?.charAt(0).toUpperCase() + user.subscription_plan?.slice(1)} Plan
                </h3>
                <p className="text-sm text-green-600">
                  Renews on {new Date(user.subscription_ends_at).toLocaleDateString()}
                </p>
              </div>
              <Button 
                onClick={() => navigate('/create-listing')}
                className="bg-green-700 hover:bg-green-800 text-white"
              >
                Create Listing
              </Button>
            </div>
          </div>
        ) : onTrial && trialDays > 0 ? (
          <div className="bg-teal-50 border border-teal-200 rounded-sm p-6 mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="font-semibold text-teal-700">
                  Free Trial - {trialDays} days remaining
                </h3>
                <p className="text-sm text-teal-600">
                  Subscribe before your trial ends to keep selling
                </p>
              </div>
              <Button 
                onClick={() => navigate('/create-listing')}
                variant="outline"
                className="border-teal-500 text-teal-600 hover:bg-teal-100"
              >
                Create Listing
              </Button>
            </div>
          </div>
        ) : onTrial && trialDays <= 0 ? (
          <div className="bg-stone-100 border border-stone-200 rounded-sm p-6 mb-8">
            <div className="text-center">
              <h3 className="font-semibold text-stone-800">Your trial has ended</h3>
              <p className="text-sm text-stone-600 mt-1">Subscribe to continue creating listings</p>
            </div>
          </div>
        ) : null}

        {/* Not a seller yet - Show Start Trial CTA */}
        {!isSeller && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-sky-500 to-teal-600 text-white rounded-sm p-8 mb-12"
          >
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
              <div className="text-center lg:text-left">
                <div className="flex items-center justify-center lg:justify-start gap-2 mb-2">
                  <Sparkles className="h-5 w-5" />
                  <span className="text-xs font-bold uppercase tracking-wider text-sky-200">Free for 30 days</span>
                </div>
                <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Start Your Selling Journey
                </h2>
                <p className="text-sky-100">
                  Try selling for free. No credit card required.
                </p>
              </div>
              <Button
                onClick={handleStartTrial}
                disabled={startingTrial}
                size="lg"
                className="bg-white text-teal-600 hover:bg-teal-50 font-semibold tracking-wide"
                data-testid="start-trial-btn"
              >
                {startingTrial ? (
                  <span className="animate-spin h-4 w-4 border-2 border-teal-500 border-t-transparent rounded-full" />
                ) : (
                  'Start Free Trial'
                )}
              </Button>
            </div>
          </motion.div>
        )}

        {/* Pricing Cards */}
        {(isSeller || subscribed) && (
          <>
            <h2 className="text-2xl font-bold text-stone-900 text-center mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
              {subscribed ? 'Your Plan' : 'Choose Your Plan'}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {plans.map((plan) => (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`bg-white border-2 rounded-sm p-6 relative cursor-pointer transition-all ${
                    selectedPlan === plan.id 
                      ? 'border-teal-500 shadow-lg' 
                      : 'border-stone-200 hover:border-stone-300'
                  } ${subscribed ? 'pointer-events-none opacity-60' : ''}`}
                  onClick={() => !subscribed && setSelectedPlan(plan.id)}
                  data-testid={`plan-${plan.id}`}
                >
                  {plan.popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-sky-500 to-teal-500 text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                      Most Popular
                    </span>
                  )}
                  {plan.savings && (
                    <span className="absolute -top-3 right-4 bg-green-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                      {plan.savings}
                    </span>
                  )}
                  
                  <div className="text-center pt-2">
                    <h3 className="text-lg font-semibold text-stone-900">{plan.name}</h3>
                    <div className="mt-4">
                      <span className="price-tag text-4xl">${plan.price}</span>
                      <span className="text-stone-500">/{plan.period}</span>
                    </div>
                  </div>

                  <div className={`mt-6 w-full h-1 rounded-full ${
                    selectedPlan === plan.id ? 'bg-gradient-to-r from-sky-500 to-teal-500' : 'bg-stone-200'
                  }`} />
                </motion.div>
              ))}
            </div>

            {/* Features */}
            <div className="bg-white border border-stone-200 rounded-sm p-8 mb-8">
              <h3 className="text-lg font-semibold text-stone-900 text-center mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                All plans include
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {features.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                      <feature.icon className="h-5 w-5 text-teal-600" />
                    </div>
                    <span className="text-stone-700">{feature.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Promo Code Input */}
            {!subscribed && (
              <div className="bg-white border border-stone-200 rounded-sm p-6 mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <Tag className="h-5 w-5 text-teal-600" />
                  <h3 className="font-semibold text-stone-900">Have a promo code?</h3>
                </div>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    placeholder="Enter promo code"
                    className="flex-1"
                    disabled={promoApplied || user?.promo_code_used}
                  />
                  {promoApplied || user?.promo_code_used ? (
                    <Button
                      variant="outline"
                      className="text-green-600 border-green-600 cursor-default"
                      disabled
                    >
                      <Check className="h-4 w-4 mr-1" />
                      Applied
                    </Button>
                  ) : (
                    <Button
                      onClick={validatePromoCode}
                      disabled={validatingPromo || !promoCode.trim()}
                      variant="outline"
                      className="border-teal-500 text-teal-600 hover:bg-teal-50"
                    >
                      {validatingPromo ? (
                        <span className="animate-spin h-4 w-4 border-2 border-teal-500 border-t-transparent rounded-full" />
                      ) : (
                        'Apply'
                      )}
                    </Button>
                  )}
                </div>
                {(promoApplied || user?.promo_code_used) && (
                  <p className="text-green-600 text-sm mt-2 flex items-center gap-1">
                    <Check className="h-4 w-4" />
                    {promoApplied?.description || `Code ${user?.promo_code_used} applied!`}
                  </p>
                )}
                
                {/* 2-Year Package Offer - shows when 2YEAR500 code is applied */}
                {promoApplied?.paid_package && (
                  <div className="mt-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                        <Sparkles className="h-6 w-6 text-amber-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-amber-800">2-Year Seller Package</h4>
                        <p className="text-sm text-amber-700">
                          One-time payment of <span className="font-bold">${promoApplied.price}</span> for {promoApplied.years} years
                        </p>
                        <p className="text-xs text-amber-600 mt-1">
                          That&apos;s only $20.83/month - Save $220 vs monthly!
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={handlePackageCheckout}
                      disabled={loading}
                      className="w-full mt-4 bg-amber-600 hover:bg-amber-700 text-white"
                    >
                      {loading ? (
                        <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      ) : (
                        `Buy 2-Year Package - $${promoApplied.price}`
                      )}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Subscribe Button - hide when package is being purchased */}
            {!subscribed && !promoApplied?.paid_package && (
              <div className="text-center">
                <Button
                  onClick={handleSubscribe}
                  disabled={loading || checkingPayment}
                  size="lg"
                  className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white font-medium tracking-wide uppercase px-12"
                  data-testid="subscribe-btn"
                >
                  {loading ? (
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    `Subscribe - $${plans.find(p => p.id === selectedPlan)?.price}/${plans.find(p => p.id === selectedPlan)?.period}`
                  )}
                </Button>
                <p className="text-sm text-stone-500 mt-3">
                  {promoApplied 
                    ? `${promoApplied.trial_days}-day free trial, then automatic billing`
                    : '30-day free trial, then automatic billing'
                  }
                </p>
                <p className="text-xs text-stone-400 mt-1">
                  Secure payment powered by Stripe. Cancel anytime.
                </p>
              </div>
            )}
          </>
        )}

        {/* FAQ */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-stone-900 text-center mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-4 max-w-2xl mx-auto">
            <div className="bg-white border border-stone-200 rounded-sm p-6">
              <h3 className="font-semibold text-stone-900">Do I need to pay to browse and buy?</h3>
              <p className="text-stone-500 mt-2">
                No! Browsing, viewing listings, and contacting sellers is completely free. 
                You only need a subscription if you want to sell items.
              </p>
            </div>
            
            <div className="bg-white border border-stone-200 rounded-sm p-6">
              <h3 className="font-semibold text-stone-900">How does the free trial work?</h3>
              <p className="text-stone-500 mt-2">
                When you decide to start selling, you get 30 days free - no credit card required.
                Create listings, message buyers, and sell items with no restrictions. After 30 days,
                choose a subscription plan to continue.
              </p>
            </div>
            
            <div className="bg-white border border-stone-200 rounded-sm p-6">
              <h3 className="font-semibold text-stone-900">Are there any commission fees?</h3>
              <p className="text-stone-500 mt-2">
                No! We don&apos;t take any commission on your sales. You keep 100% of what you earn.
                The subscription fee is the only cost.
              </p>
            </div>
            
            <div className="bg-white border border-stone-200 rounded-sm p-6">
              <h3 className="font-semibold text-stone-900">Which plan should I choose?</h3>
              <p className="text-stone-500 mt-2">
                <strong>Weekly ($3/week):</strong> Great if you&apos;re just getting started or sell occasionally.<br />
                <strong>Monthly ($7.99/month):</strong> Simple, low-cost, cancel anytime.<br />
                <strong>Yearly ($88/year):</strong> Save ~8% vs monthly.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;
